import { useId, useMemo } from 'react'
import { ADDONS, INDUSTRIES, LOCALES, PLANS, getAddon, getIndustry, getLocale, getPlan } from '@shared/catalog'
import { calculateQuote, formatMoney, isAddonAvailable, resolveRequiredAddons } from '@shared/pricing'
import type { AddonId, PlanId, QuoteInput } from '@shared/types'
import { Banner, Chip, SelectField, Segmented, Slider, Switch } from '../ui'

const LOCALE_TAGS: Record<string, string> = {
  us: 'en-US',
  eu: 'de-DE',
  uk: 'en-GB',
  in: 'en-IN',
  ae: 'en-AE',
  sg: 'en-SG',
  br: 'pt-BR',
}

export function useQuote(input: QuoteInput) {
  return useMemo(() => calculateQuote(input), [input])
}

export function money(minor: number, currency: string, minorUnits: number, localeId: string) {
  return formatMoney(minor, currency, minorUnits, LOCALE_TAGS[localeId] ?? 'en-US')
}

/** Nearest step boundary on a non-linear scale, so the slider stays usable across 3 orders of magnitude. */
const CONVERSATION_STEPS = [
  1000, 2000, 5000, 10000, 20000, 30000, 50000, 75000, 100000, 150000, 250000, 500000, 1000000,
]

export function PricingConfigurator({
  input,
  onChange,
  showIndustry = true,
  showLocale = true,
}: {
  input: QuoteInput
  onChange: (next: QuoteInput) => void
  showIndustry?: boolean
  showLocale?: boolean
}) {
  const plan = getPlan(input.planId)!
  const industry = getIndustry(input.industryId)!
  const required = resolveRequiredAddons(input.industryId)
  const bundled = new Set(plan.bundledAddons)
  // Generated rather than hardcoded so two configurators on one page cannot collide.
  const convSliderId = useId()

  const set = (patch: Partial<QuoteInput>) => onChange({ ...input, ...patch })

  const seatMax = plan.maxSeats ?? 500
  const numberMax = plan.maxNumbers ?? 200
  const convIndex = Math.max(
    0,
    CONVERSATION_STEPS.findIndex((v) => v >= input.conversations),
  )

  const toggleAddon = (id: AddonId) => {
    if (required.includes(id) || bundled.has(id)) return
    const next = new Set(input.addons)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    set({ addons: [...next] })
  }

  return (
    <div className="stack gap-6">
      {(showIndustry || showLocale) && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
            gap: '16px',
          }}
        >
          {showIndustry && (
            <SelectField
              label="Industry"
              value={input.industryId}
              help="Sets compliance requirements and preset defaults."
              onChange={(e) => {
                const nextIndustry = getIndustry(e.target.value)!
                // Switching industry re-applies its presets: a healthcare config is not a
                // valid starting point for e-commerce, and silently keeping it misleads.
                set({
                  industryId: nextIndustry.id,
                  planId: nextIndustry.presets.plan,
                  seats: nextIndustry.presets.seats,
                  numbers: nextIndustry.presets.numbers,
                  conversations: nextIndustry.presets.conversations,
                  addons: [...nextIndustry.presets.addons],
                })
              }}
            >
              {INDUSTRIES.map((i) => (
                <option key={i.id} value={i.id}>
                  {i.name}
                </option>
              ))}
            </SelectField>
          )}

          {showLocale && (
            <SelectField
              label="Billing region"
              value={input.localeId}
              help="Determines currency, tax, and residency options."
              onChange={(e) => set({ localeId: e.target.value as QuoteInput['localeId'] })}
            >
              {LOCALES.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.name} ({l.currency})
                </option>
              ))}
            </SelectField>
          )}
        </div>
      )}

      <div className="stack gap-3">
        <div className="row between wrap gap-3">
          <h3 className="title-medium">Plan</h3>
          <Segmented
            label="Billing cycle"
            value={input.billingCycle}
            onChange={(v) => set({ billingCycle: v })}
            options={[
              { value: 'monthly', label: 'Monthly' },
              { value: 'annual', label: 'Annual · save 17%' },
            ]}
          />
        </div>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: '12px',
          }}
        >
          {PLANS.map((p) => (
            <PlanTile
              key={p.id}
              planId={p.id}
              selected={input.planId === p.id}
              onSelect={() => {
                // Clamp usage into the new plan's ceilings so the quote never shows an
                // impossible configuration between render and server validation.
                const next = getPlan(p.id)!
                set({
                  planId: p.id,
                  seats: next.maxSeats ? Math.min(input.seats, next.maxSeats) : input.seats,
                  numbers: next.maxNumbers ? Math.min(input.numbers, next.maxNumbers) : input.numbers,
                  addons: input.addons.filter(
                    (a) => isAddonAvailable(a, p.id) || required.includes(a),
                  ),
                })
              }}
            />
          ))}
        </div>
      </div>

      <div className="stack gap-5">
        <h3 className="title-medium">Capacity</h3>

        <UsageRow
          label="Team seats"
          value={input.seats}
          included={plan.includedSeats}
          max={seatMax}
          min={1}
          onChange={(v) => set({ seats: v })}
          suffix="seats"
        />

        <UsageRow
          label="WhatsApp numbers"
          value={input.numbers}
          included={plan.includedNumbers}
          max={numberMax}
          min={1}
          onChange={(v) => set({ numbers: v })}
          suffix="numbers"
        />

        <div className="stack gap-2">
          <div className="row between wrap gap-2">
            <label className="label-large" htmlFor={convSliderId}>
              Monthly conversations
            </label>
            <span className="body-medium tabular">
              {input.conversations.toLocaleString()}
              <span className="text-muted"> · {plan.includedConversations.toLocaleString()} included</span>
            </span>
          </div>
          <input
            id={convSliderId}
            type="range"
            className="slider"
            min={0}
            max={CONVERSATION_STEPS.length - 1}
            step={1}
            value={convIndex}
            style={{ ['--fill' as string]: `${(convIndex / (CONVERSATION_STEPS.length - 1)) * 100}%` }}
            onChange={(e) => set({ conversations: CONVERSATION_STEPS[Number(e.target.value)] })}
          />
        </div>
      </div>

      <div className="stack gap-3">
        <div className="stack gap-1">
          <h3 className="title-medium">Modules</h3>
          {required.length > 0 && (
            <p className="body-small text-muted">
              {industry.name} requires{' '}
              {required.map((r) => getAddon(r)?.name).filter(Boolean).join(' and ')}. These stay
              selected.
            </p>
          )}
        </div>

        <div className="stack gap-2">
          {ADDONS.map((addon) => {
            const isRequired = required.includes(addon.id)
            const isBundled = bundled.has(addon.id)
            const available = isAddonAvailable(addon.id, input.planId)
            const checked = input.addons.includes(addon.id) || isBundled
            const locked = isRequired || isBundled

            return (
              <div
                key={addon.id}
                className="row between gap-4"
                style={{
                  padding: '14px 16px',
                  borderRadius: 'var(--md-shape-md)',
                  background: checked ? 'var(--md-surface-container-high)' : 'transparent',
                  boxShadow: checked ? 'none' : 'inset 0 0 0 1px var(--md-outline-variant)',
                  opacity: !available && !locked ? 0.5 : 1,
                }}
              >
                <div className="stack gap-1" style={{ minWidth: 0 }}>
                  <div className="row gap-2 wrap">
                    <span className="title-small">{addon.name}</span>
                    {isRequired && <Chip tone="warn">Required</Chip>}
                    {isBundled && !isRequired && <Chip tone="success">Included</Chip>}
                    {addon.onRequest && <Chip tone="assist">On request</Chip>}
                    {!available && !locked && (
                      <Chip tone="assist">Needs {getPlan(addon.minPlan)?.name}</Chip>
                    )}
                  </div>
                  <p className="body-small text-muted">{addon.description}</p>
                </div>
                <div className="row gap-3" style={{ flexShrink: 0 }}>
                  <span className="body-small text-muted tabular">
                    {addon.onRequest ? 'Contact sales' : isBundled ? '—' : `$${(addon.cents / 100).toLocaleString()}/mo`}
                  </span>
                  {/* No self-serve switch: it is sold through a conversation, not toggled into a
                      paid subscription for a capability that is not built yet. */}
                  {!addon.onRequest && (
                    <Switch
                      label={addon.name}
                      checked={checked}
                      disabled={locked || !available}
                      onChange={() => toggleAddon(addon.id)}
                    />
                  )}
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

function PlanTile({
  planId,
  selected,
  onSelect,
}: {
  planId: PlanId
  selected: boolean
  onSelect: () => void
}) {
  const plan = getPlan(planId)!
  return (
    <button type="button" className="selectable" aria-pressed={selected} onClick={onSelect}>
      <div className="stack gap-1">
        <div className="row between gap-2">
          <span className="title-medium">{plan.name}</span>
          {plan.popular && <Chip tone="warn">Popular</Chip>}
        </div>
        <span className="body-small" style={{ opacity: 0.8 }}>
          {plan.contactSales ? 'Custom pricing' : `From $${plan.baseCents / 100}/mo`}
        </span>
      </div>
    </button>
  )
}

function UsageRow({
  label,
  value,
  included,
  min,
  max,
  onChange,
  suffix,
}: {
  label: string
  value: number
  included: number
  min: number
  max: number
  onChange: (v: number) => void
  suffix: string
}) {
  return (
    <div className="stack gap-2">
      <div className="row between wrap gap-2">
        <span className="label-large">{label}</span>
        <span className="body-medium tabular">
          {value.toLocaleString()} {suffix}
          <span className="text-muted"> · {included} included</span>
        </span>
      </div>
      <Slider label={label} value={value} min={min} max={max} onChange={onChange} />
    </div>
  )
}

export function QuoteSummary({
  input,
  compact = false,
  collapseBreakdown = false,
}: {
  input: QuoteInput
  compact?: boolean
  /** Line-item detail starts behind a "see the breakdown" disclosure instead of always open —
   *  for contexts where the total needs to read in five seconds, with the tier/addon/industry/
   *  region detail still one click away rather than removed. */
  collapseBreakdown?: boolean
}) {
  const quote = useQuote(input)
  const locale = getLocale(input.localeId)!
  const plan = getPlan(input.planId)!
  const fmt = (v: number) => money(v, quote.currency, quote.minorUnits, input.localeId)

  const breakdown = (
    <table className="table">
      <tbody>
        {quote.lines.map((line) => (
          <tr key={line.key}>
            <td>
              <div className="stack gap-1">
                <span className="body-medium">{line.label}</span>
                {line.detail && <span className="body-small text-muted">{line.detail}</span>}
              </div>
            </td>
            <td className="num body-medium">{line.amount === 0 ? '—' : fmt(line.amount)}</td>
          </tr>
        ))}
      </tbody>
      <tfoot>
        <tr>
          <td className="title-small">Subtotal</td>
          <td className="num title-small">{fmt(quote.subtotal)}</td>
        </tr>
        {quote.discount > 0 && (
          <tr>
            <td className="body-medium">Annual discount</td>
            <td className="num body-medium" style={{ color: 'var(--md-success)' }}>
              −{fmt(quote.discount)}
            </td>
          </tr>
        )}
        {quote.tax > 0 && (
          <tr>
            <td className="body-medium">{quote.taxLabel}</td>
            <td className="num body-medium">{fmt(quote.tax)}</td>
          </tr>
        )}
        <tr>
          <td className="title-medium">Total per month</td>
          <td className="num title-medium">{fmt(quote.total)}</td>
        </tr>
      </tfoot>
    </table>
  )

  return (
    <div className="stack gap-4">
      <div className="stack gap-1">
        <span className="eyebrow">{input.billingCycle === 'annual' ? 'Billed annually' : 'Billed monthly'}</span>
        <div className="row items-baseline gap-2 wrap">
          <span className="display-small tabular">{fmt(quote.total)}</span>
          <span className="body-medium text-muted">
            per month{locale.taxRate > 0 ? ` incl. ${locale.taxLabel}` : ''}
          </span>
        </div>
        {input.billingCycle === 'annual' && quote.annualSavings > 0 && (
          <span className="body-small" style={{ color: 'var(--md-success)' }}>
            Saves {fmt(quote.annualSavings)} per year versus monthly billing.
          </span>
        )}
        {plan.contactSales && (
          <span className="body-small text-muted">Indicative. Final terms agreed with sales.</span>
        )}
      </div>

      {!compact && (
        collapseBreakdown ? (
          <details>
            <summary className="title-small" style={{ cursor: 'pointer' }}>
              See the full breakdown
            </summary>
            <div style={{ marginTop: '12px' }}>{breakdown}</div>
          </details>
        ) : (
          <>
            <hr className="divider" />
            {breakdown}
          </>
        )
      )}

      {quote.warnings.length > 0 && (
        <div className="stack gap-2">
          {quote.warnings.map((w) => (
            <Banner key={w} tone="warn">
              {w}
            </Banner>
          ))}
        </div>
      )}
    </div>
  )
}
